import { useState } from "react";
import {
  ArrayField,
  FormField,
  GroupField,
} from "../../data-model/pharmaPaFormModel";
import { AddMore } from "../../svg/add-more";
import FormConfigFieldCard from "../formConfigFieldCard/formConfigFieldCard";
import SectionWithContextMenu from "../sectionWithContextMenu/sectionWithContextMenu";
import RenderArrayFieldForFormConfig from "./renderArrayFieldForFormConfig";
import RenderGroupFieldForFormConfig from "./renderGroupFieldForFormConfig";

interface RenderFieldsProps {
  field: FormField;
  renderIndex?: number;
  parentKey?: string;
  onAddField?: (parentKey: string) => void;
  onDeleteField?: (key: string) => void;
  onEditField?: (
    parentKey?: string,
    field?: FormField,
    renderIndex?: number,
  ) => void;
}
const RenderSingleCardForFormConfig = (props: RenderFieldsProps) => {
  if (props.field.type === "group") {
    return (
      <>
        <div className="mb-2">
          <FormConfigFieldCard
            key={props?.field?.key}
            isAccordion={true}
            formField={props?.field}
            renderIndex={props?.renderIndex}
            parentKey={props?.parentKey}
            onDeleteClick={(parentKey, field, renderIndex) => {
              props?.onDeleteField?.(field?.key ?? "");
            }}
            onEditClick={(parentKey, field, renderIndex) => {
              props?.onEditField?.(
                parentKey ?? "",
                field ?? undefined,
                renderIndex ?? 0,
              );
            }}
          >
            <RenderGroupFieldForFormConfig
              field={props?.field as GroupField}
              parentKey={props?.field?.key}
              onAddField={(parentKey) => {
                props?.onAddField?.(parentKey);
              }}
              onDeleteField={(key) => {
                props?.onDeleteField?.(key);
              }}
              onEditField={(parentKey, field, renderIndex) => {
                props?.onEditField?.(
                  parentKey ?? "",
                  field ?? undefined,
                  renderIndex ?? 0,
                );
              }}
            />
          </FormConfigFieldCard>
        </div>
      </>
    );
  } else if (props.field.type === "array") {
    return (
      <>
        <div className="mb-2">
          <FormConfigFieldCard
            key={props?.field?.key}
            isAccordion={true}
            formField={props?.field}
            renderIndex={props?.renderIndex}
            parentKey={props?.parentKey}
            onDeleteClick={(parentKey, field, renderIndex) => {
              props?.onDeleteField?.(field?.key ?? "");
            }}
            onEditClick={(parentKey, field, renderIndex) => {
              props?.onEditField?.(
                parentKey ?? "",
                field ?? undefined,
                renderIndex ?? 0,
              );
            }}
          >
            <RenderArrayFieldForFormConfig
              field={props?.field as ArrayField}
              parentKey={props?.field?.key}
              onAddField={(parentKey) => {
                props?.onAddField?.(parentKey);
              }}
              onDeleteField={(key) => {
                props?.onDeleteField?.(key);
              }}
              onEditField={(parentKey, field, renderIndex) => {
                props?.onEditField?.(
                  parentKey ?? "",
                  field ?? undefined,
                  renderIndex ?? 0,
                );
              }}
            />
          </FormConfigFieldCard>
        </div>
      </>
    );
  } else {
    return (
      <div className="mb-2">
        <FormConfigFieldCard
          key={props?.field?.key}
          isAccordion={false}
          formField={props?.field}
          renderIndex={props?.renderIndex}
          parentKey={props?.parentKey}
          onDeleteClick={(parentKey, field, renderIndex) => {
            props?.onDeleteField?.(field?.key ?? "");
          }}
          onEditClick={(parentKey, field, renderIndex) => {
            props?.onEditField?.(
              parentKey ?? "",
              field ?? undefined,
              renderIndex ?? 0,
            );
          }}
        />
      </div>
    );
  }
};

export const RenderEachSectionOfTheForm = ({
  section,
  index,
  onAddField,
  onDeleteField,
  onEditField,
  onEditSection,
  onDeleteSection,
}: {
  section: FormField;
  onAddField: (parentKey: string) => void;
  onDeleteField: (key: string) => void;
  onEditField: (
    parentKey?: string,
    field?: FormField,
    renderIndex?: number,
  ) => void;
  onEditSection?: (section: string, index: number) => void;
  onDeleteSection?: (key: string) => void;
  index: number;
}) => {
  const [showFields, setShowFields] = useState(true);
  if (section.type !== "section" || !("fields" in section)) {
    return null;
  }

  const groupedFields = section.fields;

  return (
    <div
      key={section.key}
      id={`${section.key}`}
      className="single-section__container flex flex-col gap-3 rounded-md border border-primaryGray-16"
    >
      <SectionWithContextMenu
        title={section.label}
        onSectionClick={() => setShowFields(!showFields)}
        menuOptions={[
          {
            key: section.key,
            label: "Edit Section",
            onClick: (section) => {
              onEditSection?.(section.key, index);
            },
            color: "text-primaryGray-1",
          },
          {
            key: section.key,
            label: "Delete Section",
            onClick: (section) => {
              onDeleteSection?.(section.key);
            },
            color: "text-tertiaryRed-3",
          },
        ]}
      />
      <div
        className={`${showFields ? "block animate-fadeIn px-2" : "hidden p-0"} `}
      >
        {groupedFields.map((field, index) => (
          <div
            key={field.key}
            className={`single-row__container ${
              index === Object.keys(groupedFields).length - 1 ? "mb-0" : "mb-4"
            }`}
          >
            <RenderSingleCardForFormConfig
              field={field}
              renderIndex={index}
              parentKey={section.key}
              onAddField={onAddField}
              onDeleteField={onDeleteField}
              onEditField={onEditField}
            />
          </div>
        ))}
      </div>
      <div
        className="add-button mb-2 ml-2 flex items-center gap-1 text-tiny font-semibold text-tertiaryBlue-4 hover:cursor-pointer hover:text-tertiaryBlue-5"
        onClick={() => {
          onAddField(section.key);
        }}
      >
        <AddMore />
        Add Field
      </div>
    </div>
  );
};

export default RenderSingleCardForFormConfig;
